import React, { useMemo, useCallback } from 'react';
import DynamicGrid from 'components/DynamicGrid/DynamicGrid';
import DynamicGridLoading from 'components/DynamicGrid/DynamicGridLoading';
import { AddOnV3, ListingAddOnUnavailableReason } from '@kouto/types';
import AddOnCard from './AddOnCard';

interface AddOnsListProps {
  addOns: AddOnV3[];
  unavailableReasons?: Map<string, ListingAddOnUnavailableReason>;
  addOnsAmounts: Record<string, number>;
  selectedPriceTiersCount?: number;
  onAddOnsAmountsChange?: (addOns: { [key: string]: number }) => void;
  columns?: number;
  isLoading?: boolean;
}

const AddOnsList = ({
  addOns,
  unavailableReasons,
  addOnsAmounts,
  selectedPriceTiersCount = 1,
  onAddOnsAmountsChange,
  columns = 2,
  isLoading = false,
}: AddOnsListProps) => {
  const handleAmountChange = useCallback(
    (id: string, amount: number) => {
      if (addOnsAmounts[id] !== amount) {
        onAddOnsAmountsChange?.({
          ...addOnsAmounts,
          [id]: amount,
        });
      }
    },
    [addOnsAmounts, onAddOnsAmountsChange],
  );

  const gridConfig = useMemo(
    () => ({
      columnGap: 16,
      rowGap: 16,
      templateRows: [
        {
          height: 'minmax(126px, auto)',
          areas: new Array(columns).fill('').map((_, i) => i),
        },
      ],
    }),
    [columns],
  );

  if (isLoading) {
    return (
      <DynamicGridLoading
        config={gridConfig}
        skeletonCount={4}
        className="add-ons-list-loading"
      />
    );
  }

  return (
    <DynamicGrid
      config={gridConfig}
      style={{ width: '100%', paddingBottom: 1 }}
      count={addOns?.length || 0}
    >
      {addOns?.map((addOn, index) => (
        <AddOnCard
          key={addOn.listingAddOnId}
          gridArea={`A${index}`}
          selectedPriceTiersCount={selectedPriceTiersCount}
          addOn={addOn}
          unavailableReason={unavailableReasons?.get(addOn.listingAddOnId)}
          currentAmount={addOnsAmounts[addOn.listingAddOnId] || 0}
          onAmountChange={(amount: number) =>
            handleAmountChange(addOn.listingAddOnId, amount)
          }
        />
      ))}
    </DynamicGrid>
  );
};

export default AddOnsList;
